import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPinIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

// ─── Role meta (order, icons, colours) ───────────────────────────────────────
const ROLES = [
  {
    id: 'salesManager',
    icon: '📊',
    accent: { bg: 'bg-csl-green', text: 'text-csl-green', border: 'border-csl-green', light: 'bg-green-50', ring: 'ring-csl-green' },
  },
  {
    id: 'salesRep',
    icon: '🤝',
    accent: { bg: 'bg-blue-600', text: 'text-blue-600', border: 'border-blue-600', light: 'bg-blue-50', ring: 'ring-blue-500' },
  },
  {
    id: 'graphicDesigner',
    icon: '🎨',
    accent: { bg: 'bg-purple-600', text: 'text-purple-600', border: 'border-purple-600', light: 'bg-purple-50', ring: 'ring-purple-500' },
  },
  {
    id: 'communityManager',
    icon: '📱',
    accent: { bg: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500', light: 'bg-orange-50', ring: 'ring-orange-500' },
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getRoleMeta(id) {
  return ROLES.find((r) => r.id === id) || ROLES[0];
}

// ─── Main component ───────────────────────────────────────────────────────────
const Talents = () => {
  const { t } = useTranslation();

  const [activeId, setActiveId] = useState('salesManager');
  // separate displayed id so we can keep old content during exit animation
  const [displayedId, setDisplayedId] = useState('salesManager');
  const [phase, setPhase] = useState('idle'); // 'idle' | 'exit' | 'enter'
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectRole = (id) => {
    if (id === activeId) {
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(false);
    setActiveId(id);
    setPhase('exit');
    setTimeout(() => {
      setDisplayedId(id);
      setPhase('enter');
      setTimeout(() => setPhase('idle'), 350);
    }, 300);
  };

  const role = t(`talents.roles.${displayedId}`, { returnObjects: true });
  const meta = getRoleMeta(displayedId);
  const activeMeta = getRoleMeta(activeId);

  const emailSubject = t('talents.applyEmailSubject').replace('{role}', role.title);
  const emailBody = t('talents.applyEmailBody').replace(/\{role\}/g, role.title);
  const mailtoHref = `mailto:careers@csl-brands.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  // Card CSS classes based on animation phase
  const cardClass =
    phase === 'exit'
      ? 'opacity-0 translate-y-6 scale-95'
      : phase === 'enter'
      ? 'opacity-0 -translate-y-4 scale-98'
      : 'opacity-100 translate-y-0 scale-100';

  return (
    <div className="min-h-screen bg-white font-futura">
      <SEO
        path="/talents"
        title={`${t('talents.pageTitle')} | CSL Brands Careers`}
        description={t('talents.pageDescription')}
        image="/images/csl-brands-team-of-developers.jpeg"
        keywords="CSL Brands careers, jobs Cameroon, tech jobs Douala, emploi Cameroun, recrutement, sales manager, community manager, graphic designer, sales representative"
      />
      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-csl-dark-green">
        {/* decorative blobs */}
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-csl-green/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-16 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-csl-green/20 text-csl-light-green text-sm font-semibold mb-6 animate-fade-in">
            <SparklesIcon className="w-4 h-4" />
            {t('talents.badgeText')}
          </span>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 animate-slide-up leading-tight">
            {t('talents.pageTitle')}
          </h1>

          <p className="max-w-2xl mx-auto text-gray-300 text-base sm:text-lg leading-relaxed animate-fade-in">
            {t('talents.pageDescription')}
          </p>
        </div>
      </section>

      {/* ── Role pills (mobile-friendly quick nav) ───────────────────────────── */}
      <div className="bg-gray-50 border-b border-gray-200 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-3 min-w-max">
            {ROLES.map((r) => {
              const label = t(`talents.roles.${r.id}.title`);
              const isActive = r.id === activeId;
              return (
                <button
                  key={r.id}
                  onClick={() => selectRole(r.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? `${r.accent.bg} text-white shadow-md`
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <span>{r.icon}</span>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">

            {/* ── LEFT: Dropdown + description ─────────────────────────── */}
            <div className="lg:col-span-2 space-y-6">
              {/* Dropdown */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                  {t('talents.selectLabel')}
                </label>

                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen((o) => !o)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 bg-white border-2 rounded-xl text-left font-semibold transition-all duration-200 shadow-sm ${
                      dropdownOpen ? `${activeMeta.accent.border} ring-2 ${activeMeta.accent.ring}/30` : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <span className="flex items-center gap-2 text-gray-800">
                      <span className="text-xl">{activeMeta.icon}</span>
                      {t(`talents.roles.${activeId}.title`)}
                    </span>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Dropdown menu */}
                  {dropdownOpen && (
                    <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-scale-in">
                      {ROLES.map((r) => {
                        const isSelected = r.id === activeId;
                        return (
                          <button
                            key={r.id}
                            onClick={() => selectRole(r.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors duration-150 ${
                              isSelected
                                ? `${r.accent.light} ${r.accent.text} font-semibold`
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span className="text-xl">{r.icon}</span>
                            <div>
                              <div className="text-sm font-semibold leading-tight">
                                {t(`talents.roles.${r.id}.title`)}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {t(`talents.roles.${r.id}.type`)}
                              </div>
                            </div>
                            {isSelected && (
                              <CheckCircleIcon className={`w-5 h-5 ml-auto ${r.accent.text}`} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Role description (also animates) */}
              <div
                className={`transition-all duration-300 ease-out ${
                  phase === 'exit' ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
                }`}
              >
                {/* Meta badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${meta.accent.light} ${meta.accent.text}`}>
                    <BriefcaseIcon className="w-3.5 h-3.5" />
                    {role.type}
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    <BuildingOffice2Icon className="w-3.5 h-3.5" />
                    {role.department}
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    <MapPinIcon className="w-3.5 h-3.5" />
                    {role.location}
                  </span>
                </div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {role.description}
                </p>
              </div>

              {/* Apply CTA (left column, visible on mobile) */}
              <div className="lg:hidden">
                <ApplyButton mailtoHref={mailtoHref} label={t('talents.applyButton')} accent={meta.accent} />
              </div>
            </div>

            {/* ── RIGHT: Animated role card ─────────────────────────────── */}
            <div className="lg:col-span-3">
              <div
                className={`transition-all duration-300 ease-out ${cardClass}`}
              >
                <div
                  className={`bg-white rounded-2xl shadow-xl border-t-4 ${meta.accent.border} overflow-hidden`}
                >
                  {/* Card header */}
                  <div className={`${meta.accent.light} px-6 sm:px-8 py-6`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-2xl ${meta.accent.bg} flex items-center justify-center text-3xl shadow-md flex-shrink-0`}>
                        {meta.icon}
                      </div>
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                          {role.title}
                        </h2>
                        <p className={`text-sm font-semibold mt-1 ${meta.accent.text}`}>
                          {role.department} · {role.type}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                          <MapPinIcon className="w-3.5 h-3.5" />
                          {role.location}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="px-6 sm:px-8 py-6 space-y-6">
                    {/* Responsibilities */}
                    <div>
                      <h3 className={`text-sm font-bold uppercase tracking-widest mb-3 ${meta.accent.text}`}>
                        {t('talents.cardLabels.responsibilities')}
                      </h3>
                      <ul className="space-y-2.5">
                        {(role.responsibilities || []).map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                            <span className={`w-5 h-5 rounded-full ${meta.accent.bg} flex-shrink-0 flex items-center justify-center mt-0.5`}>
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Requirements */}
                    <div>
                      <h3 className={`text-sm font-bold uppercase tracking-widest mb-3 ${meta.accent.text}`}>
                        {t('talents.cardLabels.requirements')}
                      </h3>
                      <ul className="space-y-2.5">
                        {(role.requirements || []).map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                            <span className={`w-2 h-2 rounded-full ${meta.accent.bg} flex-shrink-0 mt-1.5`} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Card footer CTA (desktop) */}
                  <div className="hidden lg:block px-6 sm:px-8 pb-6">
                    <ApplyButton mailtoHref={mailtoHref} label={t('talents.applyButton')} accent={meta.accent} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Apply section (full-width band) ──────────────────────────────────── */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-csl-green to-csl-dark-green relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <EnvelopeIcon className="w-10 h-10 text-white/70 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            {t('talents.applyTitle')}
          </h2>
          <p className="text-white/80 mb-8 text-sm sm:text-base leading-relaxed">
            {t('talents.applyDescription')}
          </p>
          <a
            href={mailtoHref}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-csl-green font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 text-sm sm:text-base"
          >
            <EnvelopeIcon className="w-5 h-5" />
            {t('talents.applyButton')}
          </a>
          <p className="mt-4 text-white/50 text-xs">careers@csl-brands.com</p>
        </div>
      </section>
    </div>
  );
};

// ─── Reusable apply button ────────────────────────────────────────────────────
function ApplyButton({ mailtoHref, label, accent }) {
  return (
    <a
      href={mailtoHref}
      className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 ${accent.bg} text-white font-semibold rounded-xl hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 text-sm sm:text-base`}
    >
      <EnvelopeIcon className="w-5 h-5" />
      {label}
    </a>
  );
}

export default Talents;
